import { Box, useRadioGroup, Wrap } from '@chakra-ui/react'
import React, { useState } from 'react'

import { FormContext, type FormInputType } from '@/components/UI/Form/types'

import OnboardingInputRadioItem from './OnboardingInputRadioItem'

const OnboardingInputRadio: React.FC<{
  input: FormInputType
}> = ({ input }) => {
  if (input.type !== 'radio') {
    throw new Error('Invariant violation')
  }

  // Because standard radios don't support "unchecking" options, we need
  // to handle checking/unchecking ourselves.
  const [checkedValue, setCheckedValue] = useState<string | null>(input.initialValue ?? null)

  const { name: inputName } = input
  const { submitting, direction, onChange } = React.useContext(FormContext)
  const handleChange = React.useCallback(
    (newValue: string) => {
      // Allow unchecking optional radio
      const shouldUncheckValue = input.required === false && newValue === checkedValue
      if (shouldUncheckValue) {
        setCheckedValue(null)
        onChange(inputName, null)
      } else {
        setCheckedValue(newValue)
        onChange(inputName, newValue)
      }
    },
    [checkedValue, input.required, inputName, onChange]
  )

  const disabled = (submitting || input.disabled) ?? false

  const { getRootProps, getRadioProps } = useRadioGroup({
    name: input.name,
    onChange: handleChange,
    defaultValue: input.initialValue ?? undefined,
  })

  return (
    <Box {...getRootProps()}>
      <Wrap direction={{ base: 'column', md: 'row' }} spacing={4}>
        {input.options.map(item => (
          <OnboardingInputRadioItem
            key={item.value}
            label={item.label}
            direction={input.direction ?? direction}
            radioPayload={{
              ...getRadioProps({
                value: item.value,
                disabled,
              }),
              isChecked: item.value === checkedValue,
            }}
          />
        ))}
      </Wrap>
    </Box>
  )
}

export default OnboardingInputRadio
